<template>
  <div>
    <div class="mt-3 flex space-x-3 py-2">
      <UISwitch
        id="sms-notification-switch"
        v-model:value="isNotificationsEnabled"
        :aria-label="t('alt_text.sms_notification_switch')"
        :title="t('alt_text.sms_notification_switch')"
        class="w-1/3"
        size="small"
        @update:value="
          value => {
            toggleNotification(value)
          }
        "
      />
      <div class="items-center text-sm font-medium text-gray-700">
        {{
          isNotificationsEnabled
            ? t('eventNotification.enabled')
            : t('eventNotification.disabled')
        }}
      </div>
    </div>
    <div class="my-2 text-sm font-medium text-gray-500">
      {{ t('eventNotification.sms.notificationLabel') }}
    </div>

    <!-- Render all SMS templates of NotificationType - confirmation,reminder and others -->
    <UITooltip :trigger="!isNotificationsEnabled ? 'hover' : 'manual'">
      <template #trigger>
        <div :class="{ 'disabled-div': !isNotificationsEnabled }">
          <div
            v-for="(smsNotification, i) in state.smsNotifications"
            :key="'sms-notification-' + i"
          >
            <!-- Render a single SMS configurator window -->
            <SmsTemplateConfigurator
              ref="smsTemplateConfigurator"
              :index="i"
              :class="{
                'rounded-t-lg border': i === 0,
                'rounded-b-lg border': i === state.smsNotifications.length - 1,
              }"
              :notification="smsNotification"
              :is-notifications-enabled="isNotificationsEnabled"
            />
          </div>
        </div>
      </template>
      <div>{{ t('eventNotification.tooltip') }}</div>
    </UITooltip>
  </div>
</template>

<script setup lang="ts">
import { EventNotificationService } from '@/class/services/EventNotificationService'
import { useNotify } from '@/components/composition/notification'
import {
  getEventNotificationState,
  eventNotificationState as state,
  updateNotificationState,
} from '@/states/EventNotificationState'
import { UISwitch, UITooltip } from '@gohighlevel/ghl-ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SmsTemplateConfigurator from './SmsTemplateConfigurator.vue'
const { t } = useI18n()
const { sendNotification } = useNotify()

const isNotificationsEnabled = ref(true)
const smsTemplateConfigurator = ref(null) as any
defineExpose({ validateAll })
const emit = defineEmits(['update-notification'])

isNotificationsEnabled.value = getEventNotificationState('sms')

/**
 *
 * @param value boolean
 * enable/disable all the SMS notification under this
 */
async function toggleNotification(value) {
  const payload = state.smsNotifications.map(notification => {
    return { ...notification, ...{ deleted: !value } }
  })
  state.disableSave = true
  try {
    isNotificationsEnabled.value = value

    const payloadToUpdate = payload.filter(notification => notification._id)
    const payloadToCreate = payload
      .filter(notification => !notification._id)
      .map((notification: any) => {
        delete notification._id
        return notification
      })
    if (payloadToUpdate.length) {
      const { data } = await EventNotificationService.updateEventNotifications(
        payloadToUpdate
      )
      updateNotificationState(data)
    }
    if (payloadToCreate.length) {
      const { data } = await EventNotificationService.createEventNotifications(
        payloadToCreate
      )
      updateNotificationState(data)
    }
  } catch (error) {
    sendNotification({
      type: 'error',
      title: 'Failed to update SMS notification',
      icon: 'AlertCircleIcon',
    })
    return
  } finally {
    state.disableSave = false
  }
  //update calendar notification if enabled
  handleOuterNotification(value, payload[0].notificationType)
  sendNotification({
    type: 'success',
    title: value ? 'SMS Notifications Enabled!' : 'SMS Notifications Disabled!',
    icon: 'CheckCircleIcon',
  })
}

function handleOuterNotification(value, type) {
  emit('update-notification', {
    channel: 'sms',
    type: type,
    status: value,
  })
}

function validateAll() {
  if (smsTemplateConfigurator.value) {
    return Promise.all(
      smsTemplateConfigurator.value.map(formComponent => {
        if (formComponent && formComponent.validateForm) {
          return formComponent.validateForm()
        }
      })
    )
  }
  return Promise.resolve()
}
</script>

<style scoped>
.disabled-div {
  position: relative;
  opacity: 0.5;
}

.disabled-div input,
.disabled-div textarea,
.disabled-div button,
.disabled-div select {
  pointer-events: none;
  user-select: none;
}

.disabled-div .ui-checkbox,
.disabled-div .ui-switch {
  pointer-events: none;
}
</style>
